import { MemoryTab } from '@apps/web/components/memory/memory_tab';
import { cn } from '@apps/web/lib/utils';

type ProfileNotesSectionProps = {
    readonly organizationId: string;
    readonly entityId: string;
    readonly entityType: 'caregiver' | 'client';
    readonly externalProfileNote: string | null | undefined;
    readonly externalSourceLabel: string | undefined;
    // Extension surfaces are too narrow for the side-by-side layout.
    readonly stacked?: boolean;
};

/**
 * Phoebe's compiled memory beside the source EHR's read-only profile note.
 * Keeping both here makes their provenance distinct while presenting all
 * scheduler-authored and EHR-authored context in one place.
 */
export function ProfileNotesSection({
    organizationId,
    entityId,
    entityType,
    externalProfileNote,
    externalSourceLabel,
    stacked = false,
}: ProfileNotesSectionProps) {
    const memory = (
        <MemoryTab
            organizationId={organizationId}
            entityId={entityId}
            entityType={entityType}
        />
    );

    // Trim only to decide whether a note exists. Render the original text so
    // the whitespace from the EHR survives.
    if (!externalProfileNote || externalProfileNote.trim() === '') {
        return memory;
    }
    return (
        <div
            className={cn(
                'grid items-start gap-6',
                !stacked && 'lg:grid-cols-2',
            )}
        >
            <div className="min-w-0">{memory}</div>
            <section
                className="min-w-0"
                data-testid={`${entityType}-profile-note`}
            >
                <div className="mb-2 flex items-baseline justify-between gap-3">
                    <h3 className="text-sm font-semibold">Profile note</h3>
                    <span className="text-muted-foreground shrink-0 text-xs">
                        Synced from {externalSourceLabel ?? 'EHR'}
                    </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">
                    {externalProfileNote}
                </p>
            </section>
        </div>
    );
}
